import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiClock, FiCheckCircle, FiChevronRight } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { challengesData } from '../data/challenges';

const QuickPlayModal = ({ isOpen, onClose, onComplete, mode }) => {
  const [quickCards, setQuickCards] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [completedQuick, setCompletedQuick] = useState([]);

  useEffect(() => {
    if (isOpen) {
      const shuffled = [...challengesData].sort(() => Math.random() - 0.5);
      setQuickCards(shuffled.slice(0, 3));
      setCurrentIndex(0);
      setCompletedQuick([]);
    }
  }, [isOpen]);

  if (!isOpen || quickCards.length === 0) return null;

  const currentCard = quickCards[currentIndex];
  const isCurrentCompleted = completedQuick.includes(currentCard.id);

  const handleComplete = () => {
    if (!isCurrentCompleted) {
      setCompletedQuick([...completedQuick, currentCard.id]);
      onComplete(currentCard.id);
    }
  };

  const handleNext = () => {
    if (currentIndex < quickCards.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      onClose();
    }
  };

  return (
    <AnimatePresence>
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/70 backdrop-blur-md"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          className="bg-white rounded-[2rem] shadow-2xl w-full max-w-lg overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-6 bg-gradient-to-r from-cyan-500 to-blue-500 text-white relative">
            <button
              onClick={onClose}
              className="absolute top-6 right-6 p-2 text-white/70 hover:text-white transition-colors"
            >
              <SafeIcon icon={FiX} className="w-6 h-6" />
            </button>

            <div className="flex items-center gap-3 mb-4">
              <SafeIcon icon={FiClock} className="w-6 h-6" />
              <h2 className="text-2xl font-black">Quick Play</h2>
            </div>

            <div className="flex gap-2">
              {quickCards.map((card, idx) => (
                <div
                  key={card.id}
                  className={`flex-1 h-1.5 rounded-full transition-all ${
                    idx <= currentIndex ? 'bg-white' : 'bg-white/30'
                  }`}
                />
              ))}
            </div>
          </div>

          <div className="p-8 space-y-6">
            <div className="flex items-center gap-4">
              <div className={`w-14 h-14 rounded-xl ${currentCard.gradient} text-white shadow-md flex items-center justify-center`}>
                <SafeIcon icon={currentCard.icon} className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-xl font-black text-slate-900">{currentCard.title}</h3>
                <p className="text-xs text-slate-500 font-medium">
                  Card {currentIndex + 1} of {quickCards.length}
                </p>
              </div>
            </div>

            <div className="space-y-3">
              <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                Question
              </h4>
              <p className="text-xl font-black text-slate-900 leading-tight">
                {currentCard.prompt}
              </p>
            </div>

            <div className="p-5 bg-slate-50 rounded-xl border border-slate-100">
              <p className="text-sm text-slate-600 leading-relaxed font-medium italic">
                {currentCard.reflection}
              </p>
            </div>

            <div className="flex gap-3">
              {!isCurrentCompleted && (
                <button
                  onClick={handleComplete}
                  className="flex-1 py-4 rounded-xl bg-emerald-500 text-white font-black text-sm flex items-center justify-center gap-2 hover:bg-emerald-600 transition-colors active:scale-95"
                >
                  <SafeIcon icon={FiCheckCircle} />
                  Mark Complete
                </button>
              )}

              <button
                onClick={handleNext}
                className={`${
                  isCurrentCompleted ? 'flex-1' : 'flex-none px-6'
                } py-4 rounded-xl bg-indigo-500 text-white font-black text-sm flex items-center justify-center gap-2 hover:bg-indigo-600 transition-colors active:scale-95`}
              >
                {currentIndex < quickCards.length - 1 ? (
                  <>
                    Next
                    <SafeIcon icon={FiChevronRight} />
                  </>
                ) : (
                  'Finish'
                )}
              </button>
            </div>

            {isCurrentCompleted && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center text-emerald-600 font-bold text-sm"
              >
                ✓ Spark collected!
              </motion.div>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default QuickPlayModal;